import type { FeatureName } from "types/features";

import type {
  SHARED_CONSTANTS_PERMISSIONS_BY_VALUE,
  SHARED_CONSTANTS_PERMISSION_ACTIONS,
  SHARED_CONSTANTS_PERMISSION_RESOURCE_GROUPS,
} from "@lib/shared_constants.json";

// The correctness of these actions should be confirmed and they should either be removed or added to shared_constants
type ACTIONS_TO_REVIEW = {
  send: "send";
  admin: "admin";
  launch: "launch";
  search: "search";
  manage: "manage";
};

export type Permission = keyof SHARED_CONSTANTS_PERMISSIONS_BY_VALUE;
export type PermissionValues = SHARED_CONSTANTS_PERMISSIONS_BY_VALUE[Permission];
export type PermissionAction = keyof SHARED_CONSTANTS_PERMISSION_ACTIONS | keyof ACTIONS_TO_REVIEW;
export type PermissionResourceGroup = keyof SHARED_CONSTANTS_PERMISSION_RESOURCE_GROUPS;
export type PermissionResource = {
  [G in PermissionResourceGroup]: keyof SHARED_CONSTANTS_PERMISSION_RESOURCE_GROUPS[G];
}[PermissionResourceGroup];

export type PermissionConfig = {
  permission: PermissionValues;
} & (
  | { display_name: string }
  | { dynamic_display_name: (featureOn: (feature: FeatureName) => boolean, locale: Intl.Locale) => string }
) & {
    /**
     * `feature_flipper` should be empty if the permission should always be shown,
     * or a list of feature flippers whose statuses should each match their
     * respective `match_value` in order to be shown.
     * @see shouldShowPermission in usePermissionConfigs.ts
     */
    feature_flipper: null | { name: FeatureName; match_value: boolean }[];
    helper_text: null | string;
    enabled_for_territories: boolean;
    granular_permission_of?: PermissionValues;
    resource?: null | PermissionResource;
    action?: null | PermissionAction;
    sensitive_tooltip_text?: string;
    definition?: null | string;
    /**
     * If the permission is deprecated, it should not be shown in the UI.
     */
    deprecated?: boolean;
  };

/**
 * These are constant strings for the benefit of generating permission check method names in Developer.ko.d.ts
 * and similar files
 * These represent the different types of actions that can be gated by permissions
 */
export const PERMISSION_ACTIONS: Record<PermissionAction, string> = {
  admin: "Admin",
  allowlist: "Allowlist",
  approve: "Approve",
  blocklist: "Blocklist",
  delete: "Delete",
  export: "Export",
  import: "Import",
  manage: "Manage",
  merge: "Merge",
  publish: "Publish",
  replace: "Replace",
  search: "Search",
  send: "Send",
  suspend: "Suspend",
  view: "View",
  edit: "Edit",
  archive: "Archive",
  launch: "Launch",
};

export type PermissionActionValue = (typeof PERMISSION_ACTIONS)[PermissionAction];
